import { createSelector } from '@reduxjs/toolkit';
import type { AutoConfiguratorState } from '@snap-types/models/auto-configurator/state';
import { quantityBreakColumns } from '@ui/features/alpha/config/columns';
import { selectFields } from '@ui/features/alpha/state/selectors/field';
import { fieldIdToValueId } from '@ui/features/alpha/utilities/field-id';

import { getLineItem } from '../../logic/line-item';
import { getFirstQuantityBreak } from '../../logic/quantity-break';

export const selectQuantityBreaks = createSelector(
  [
    (state: AutoConfiguratorState, lineItemUuid: string) =>
      getLineItem(state, lineItemUuid).quantityBreakUuids,
    (state: AutoConfiguratorState) => state.quantityBreaks,
  ],
  (quantityBreakUuids, quantityBreaks) =>
    quantityBreakUuids.map((uuid) => quantityBreaks[uuid]),
);

export const selectNonFirstQuantityBreaks = createSelector(
  [
    (state: AutoConfiguratorState, lineItemUuid: string) =>
      selectQuantityBreaks(state, lineItemUuid),
  ],
  (quantityBreaks) => quantityBreaks.slice(1),
);

export function selectQuantityBreak(
  state: AutoConfiguratorState,
  lineItemUuid: string,
  quantityBreakUuid: string,
) {
  const lineItem = getLineItem(state, lineItemUuid);

  if (!lineItem.quantityBreakUuids.includes(quantityBreakUuid)) {
    throw new Error('Line does not contain quantity break');
  }

  return state.quantityBreaks[quantityBreakUuid];
}

export function selectFirstQuantityBreak(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  return getFirstQuantityBreak(state, lineItemUuid);
}

export function selectQuantityBreakEvaluationStatus(
  state: AutoConfiguratorState,
  lineItemUuid: string,
  quantityBreakUuid: string,
) {
  return selectQuantityBreak(state, lineItemUuid, quantityBreakUuid)
    .evaluationStatus;
}

/**
 * What the system arrived at for a break, whether or not it was overridden.
 * Null until a calculation has come back.
 */
export function selectQuantityBreakCalculatedData(
  state: AutoConfiguratorState,
  quantityBreakUuid: string,
) {
  return state.quantityBreaks[quantityBreakUuid]?.calculatedData ?? null;
}

const quantityBreakFieldIds = quantityBreakColumns.map(
  (column) => column.fieldId,
);

export const selectQuantityBreakCells = createSelector(
  [
    (_: AutoConfiguratorState, lineItemUuid: string) => lineItemUuid,
    (_: AutoConfiguratorState, __: string, quantityBreakUuid: string) =>
      quantityBreakUuid,
    (state: AutoConfiguratorState) =>
      selectFields(state, quantityBreakFieldIds),
  ],
  (lineItemUuid, quantityBreakUuid, fields) => {
    return quantityBreakColumns.map((column, index) => {
      const uuid =
        column.fieldId.model === 'lineItem' ? lineItemUuid : quantityBreakUuid;

      return {
        column,
        field: fields[index],
        valueId: fieldIdToValueId(column.fieldId, uuid),
      };
    });
  },
);
